/* eslint-disable react/display-name */
import React from 'react';
import {Totals} from '../../../../../common/components/Totals/Totals';
import {TotalsSectionDataHooks} from '../TotalsSection';
import {useControllerProps} from '../../../Widget/ControllerContext';
import {TotalUpdateSkeleton} from '../../TotalUpdateSkeleton';
import {useExperiments, useEnvironment} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../../../common/constants';
import {formatSignedAmount, isFreeShippingDiscount} from '../../../../../domain/utils/total.util';
import {useLocaleKeys} from '../../../../../locale-keys/LocaleKeys';
import {TagSmall} from '@wix/wix-ui-icons-common';
import {classes} from './DiscountRows.st.css';
import {Text} from '@wix/wix-ui-tpa/cssVars';
import {DiscountModel} from '../../../../../domain/models/checkout/CartSummary/Discount.model';
import {classes as totalsRowClasses} from '../../../../../common/components/Totals/Partials/TotalsRow/TotalsRow.st.css';

export enum DiscountRowsDataHook {
  DiscountIcon = 'DiscountRows.DiscountIcon',
  DiscountName = 'DiscountRows.DiscountName',
  DiscountValue = 'DiscountRows.DiscountValue',
}

export const DiscountRows = () => {
  const {experiments} = useExperiments();
  const {isCartSummaryUpdating, cart, cartSummary} = useControllerProps().checkoutStore;
  const {isRTL} = useEnvironment();
  const localeKeys = useLocaleKeys();
  const couponAlignmentInCartAndCheckoutEnabled = experiments.enabled(SPECS.CouponAlignmentInCartAndCheckout);
  const globalDiscounts = cartSummary?.globalDiscounts;

  const getDiscountValue = (discount?: DiscountModel) => {
    if (isCartSummaryUpdating || !cartSummary) {
      return () => <TotalUpdateSkeleton />;
    }

    if (isFreeShippingDiscount(cartSummary, couponAlignmentInCartAndCheckoutEnabled)) {
      return localeKeys.checkout.order_summary.totals.discount.free_delivery();
    }

    return formatSignedAmount({
      amount: couponAlignmentInCartAndCheckoutEnabled
        ? discount?.totalAmountOff ?? /* istanbul ignore next */ ''
        : cartSummary.priceSummary.discount.formattedAmount,
      isRTL,
    });
  };

  const titleWithIcon = (discount?: DiscountModel) => (
    <div className={classes.discountTag}>
      <TagSmall aria-hidden className={classes.discountIcon} data-hook={DiscountRowsDataHook.DiscountIcon} />
      <Text data-hook={DiscountRowsDataHook.DiscountName} className={classes.discountNameText}>
        {couponAlignmentInCartAndCheckoutEnabled ? discount?.name : cart.coupon?.code}
      </Text>
    </div>
  );

  if (couponAlignmentInCartAndCheckoutEnabled) {
    if (globalDiscounts?.length === 1) {
      const discount = globalDiscounts[0];
      return (
        <Totals.TotalsRow
          dataHook={TotalsSectionDataHooks.DiscountRow}
          title={() => titleWithIcon(discount)}
          value={getDiscountValue(discount)}
        />
      );
    }

    return (
      <Totals.TotalsSection title={localeKeys.checkout.discounts()} dataHook={TotalsSectionDataHooks.DiscountsSection}>
        {globalDiscounts?.map((discount, index) => (
          <Totals.TotalsRow
            isSecondary
            dataHook={TotalsSectionDataHooks.DiscountRow}
            className={totalsRowClasses.groupedRow}
            key={`${discount.name}-${discount.sourceType}-${index}`}
            title={() => titleWithIcon(discount)}
            value={getDiscountValue(discount)}
          />
        ))}
      </Totals.TotalsSection>
    );
  } else {
    return (
      <Totals.TotalsRow
        dataHook={TotalsSectionDataHooks.DiscountRow}
        title={() => titleWithIcon()}
        value={getDiscountValue()}
      />
    );
  }
};
